import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { BetterAuthError } from "../utils";
import type { RegisterSsoProviderInput, RegisterSsoProviderResponse } from "./schemas";
import { SSO_PROVIDERS_QUERY_KEY } from "./useSsoProviders";

export function useRegisterSsoProvider(
  options?: UseMutationOptions<
    RegisterSsoProviderResponse,
    BetterAuthError,
    RegisterSsoProviderInput,
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async (input: RegisterSsoProviderInput): Promise<RegisterSsoProviderResponse> => {
      const { data, error } = await authClient.sso.register(input);
      if (error) {
        if (error.code === "discovery_untrusted_origin") {
          throw new Error(
            "For security reasons, the issuer URL is not trusted. Either use a different issuer URL or expand advanced options, disable auto-discovery and manually specify the endpoints.",
          );
        }
        if (
          error.message === "You must be an organization owner or admin to register SSO providers"
        ) {
          throw new Error("You must be a company admin to register an SSO provider");
        }
        throw new Error(betterAuthErrorText(error, "Could not register SSO provider"));
      }
      return data;
    },
    onSuccess: async (...args) => {
      // Callers navigate to the provider list in their own onSuccess, so refresh it first or it
      // renders the pre-registration cache; "all" covers the list having no observers by then.
      await queryClient.invalidateQueries({
        queryKey: SSO_PROVIDERS_QUERY_KEY,
        refetchType: "all",
      });
      onSuccess?.(...args);
    },
  });
}
